/**
 * IndustryStep - Multi-select NAICS-based business classification
 *
 * A business can span multiple NAICS sectors (e.g., ALF that also does
 * home care + dental). Multi-select adds more nodes/edges to the tenant graph.
 *
 * When a product is already selected, the product's industry is pre-filled
 * but the user can add additional sectors for richer classification.
 *
 * Uses NAICS (North American Industry Classification System) codes
 * matching what the US government requires for business classification.
 */

import React, { useMemo } from 'react';
import {
  Stack,
  Text,
  MultiSelect,
  Select,
  Badge,
  Group,
  Alert,
  Paper,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconCreditCard,
  IconPackage,
} from '@tabler/icons-react';
import {
  NAICS_SECTOR_OPTIONS,
  getSubsectorOptions,
  getSectorLabel,
  sectorHasPayers,
  getComplianceHints,
} from '../data/naicsCodes';

export interface IndustryStepProps {
  /** Selected NAICS sector codes (multi-select). */
  naicsSectors: string[];
  /** Primary subsector code (optional refinement of first sector). */
  naicsSubsector?: string;
  /** Product industry hint — pre-fills if set. */
  productIndustry?: string;
  /** Product name for context display. */
  productName?: string;
  onSectorsChange: (sectors: string[]) => void;
  onSubsectorChange: (subsector: string | null) => void;
}

/**
 * Maps product industry identifiers to NAICS sector codes.
 * Products use human-readable industry names; NAICS uses numeric codes.
 */
const INDUSTRY_TO_NAICS: Record<string, string> = {
  assisted_living: '62',
  home_care: '62',
  dental: '62',
  outpatient_rehab: '62',
  hospital: '62',
  pharmacy: '62',
  mental_health: '62',
  finance: '52',
  insurance: '52',
  construction: '23',
  food_service: '72',
  accounting: '54',
  legal: '54',
};

export function IndustryStep({
  naicsSectors,
  naicsSubsector,
  productIndustry,
  productName,
  onSectorsChange,
  onSubsectorChange,
}: IndustryStepProps) {
  // Primary sector for subsector cascading
  const primarySector = naicsSectors[0] ?? null;
  const subsectorOptions = primarySector ? getSubsectorOptions(primarySector) : [];

  // Aggregate compliance hints from ALL selected sectors
  const allComplianceHints = useMemo(() => {
    const hints = new Set<string>();
    for (const sector of naicsSectors) {
      for (const hint of getComplianceHints(sector)) {
        hints.add(hint);
      }
    }
    return Array.from(hints);
  }, [naicsSectors]);

  // Check if any selected sector has payer programs
  const anyHasPayers = useMemo(
    () => naicsSectors.some(sectorHasPayers),
    [naicsSectors]
  );

  // Product pre-fill hint
  const productNaics = productIndustry ? INDUSTRY_TO_NAICS[productIndustry] : null;

  return (
    <Stack gap="lg" py="md">
      <Text size="lg" fw={500}>
        What type of business are you running?
      </Text>
      <Text size="sm" c="dimmed">
        Select one or more industry sectors. A business can span multiple sectors
        (e.g., assisted living + home care + dental). Each adds relevant workflows
        and compliance rules.
      </Text>

      {/* Product pre-fill context */}
      {productName && productNaics && (
        <Alert variant="light" color="blue" icon={<IconPackage size={16} />}>
          <Text size="sm">
            <Text span fw={500}>{productName}</Text> auto-selected{' '}
            <Text span fw={500}>{getSectorLabel(productNaics)}</Text>.
            {' '}Add more sectors below to enrich your configuration.
          </Text>
        </Alert>
      )}

      {/* Multi-select Sectors */}
      <MultiSelect
        label="Industry Sectors"
        placeholder="Search or select your industry sectors..."
        data={NAICS_SECTOR_OPTIONS}
        value={naicsSectors}
        onChange={(values) => {
          onSectorsChange(values);
          // Clear subsector if primary sector changed
          if (values[0] !== primarySector) {
            onSubsectorChange(null);
          }
        }}
        searchable
        clearable
        size="md"
        maxValues={5}
        description="Select up to 5 NAICS sectors"
      />

      {/* Subsector Select (cascading from primary sector) */}
      {primarySector && subsectorOptions.length > 1 && (
        <Select
          label={`Subsector of ${getSectorLabel(primarySector)}`}
          placeholder="Narrow down your primary industry..."
          data={subsectorOptions}
          value={naicsSubsector ?? null}
          onChange={onSubsectorChange}
          searchable
          clearable
          size="md"
        />
      )}

      {/* Selected sectors summary */}
      {naicsSectors.length > 0 && (
        <Paper p="sm" withBorder bg="gray.0" radius="md">
          <Text size="xs" fw={600} mb="xs">Selected Sectors</Text>
          <Group gap="xs" wrap="wrap">
            {naicsSectors.map((sector) => (
              <Badge
                key={sector}
                size="sm"
                variant="light"
                color={productNaics === sector ? 'blue' : 'gray'}
              >
                {getSectorLabel(sector)} ({sector})
                {productNaics === sector && ' — from product'}
              </Badge>
            ))}
          </Group>
        </Paper>
      )}

      {/* Compliance hints from ALL sectors */}
      {allComplianceHints.length > 0 && (
        <Group gap="xs" wrap="wrap">
          <IconShieldCheck size={14} color="var(--mantine-color-orange-6)" />
          <Text size="xs" c="dimmed">Likely compliance:</Text>
          {allComplianceHints.map((hint) => (
            <Badge key={hint} size="sm" variant="light" color="orange">
              {hint}
            </Badge>
          ))}
        </Group>
      )}

      {/* Payer programs badge */}
      {anyHasPayers && (
        <Group gap="xs">
          <IconCreditCard size={14} color="var(--mantine-color-grape-6)" />
          <Badge size="sm" variant="light" color="grape">
            Payer Programs Available
          </Badge>
          <Text size="xs" c="dimmed">
            Medicaid, Medicare, VA, and commercial insurance billing
          </Text>
        </Group>
      )}
    </Stack>
  );
}

export default IndustryStep;
